<template>
  <div class="app-layout" :style="layoutStyle">
    <el-container class="layout-container" direction="vertical">
      <el-header class="layout-header" height="auto">
        <Header ref="headerRef" @toggle-sidebar="handleToggleSidebar" />
      </el-header>

      <el-container
        class="main-container"
        :class="{ 'tree-mode': menuMode === 'tree', 'flow-mode': menuMode === 'flow' }"
      >
        <!-- 移动端展开侧边栏的悬浮箭头 -->
        <div 
          v-if="isMobile && !showSidebar && menuMode === 'tree'" 
          class="mobile-sidebar-trigger" 
          @click="toggleSidebar"
        >
          <i class="el-icon-arrow-right"></i>
        </div>

        <!-- 移动端遮罩层 -->
        <div 
          v-if="isMobile && showSidebar && menuMode === 'tree'" 
          class="sidebar-mask" 
          @click="closeSidebar"
        ></div>

        <!-- 侧边栏 -->
        <div
          v-if="menuMode === 'tree' && showSidebar"
          class="layout-sidebar"
          :width="sidebarWidth"
          :class="{ 'mobile-collapsed': isMobile && !sidebarExpanded, 'mobile-expanded': isMobile && sidebarExpanded }"
        >
          <Sidebar
            :nav-menus="navMenus"
            :collapsed="isMobile && !sidebarExpanded"
            @menu-children-change="handleMenuChildrenChange"
            @show-sidebar="showSidebar = true"
            @hide-sidebar="showSidebar = false"
          />
        </div>

        <!-- 右侧布局区域 -->
        <div class="right-layout-area">
          <!-- 面包屑 -->
          <Bottom class="layout-bottom" />

          <el-main
            ref="mainContent"
            class="layout-content"
            :class="{
              'flow-main': menuMode === 'flow',
              'tree-with-sidebar': menuMode === 'tree' && showSidebar,
              'tree-no-sidebar': menuMode === 'tree' && !showSidebar
            }"
          >
            <div class="content-wrapper">
              <div class="router-view-container">
                <transition name="zoom" mode="out-in">
                  <router-view :key="$route.fullPath" class="router-view" />
                </transition>
              </div>
              <transition name="zoom" mode="out-in">
                <RouterMenu
                  v-if="showRouterMenu && currentChildren.length > 0"
                  :key="`router-menu-${currentChildren.length}`"
                  :children-menus="currentChildren"
                  :is-mobile="isMobile"
                  class="router-menu-component"
                />
              </transition>
            </div>
          </el-main>
        </div>
      </el-container>
    </el-container>
  </div>
</template>

<script>
import Header from './header.vue'
import Sidebar from './sidebar.vue'
import RouterMenu from './Router.vue'
import Bottom from './bottom.vue'
import { EventBus } from '@/directive/event-bus.js'

export default {
  components: {
    Header,
    Sidebar,
    RouterMenu,
    Bottom
  },
  data() {
    return {
      menuMode: 'tree',
      navMenus: [],
      showRouterMenu: false,
      currentChildren: [],
      showSidebar: false,
      currentBackground: null,
      isMobile: false,
      sidebarExpanded: true,
      resizeTimeout: null
    }
  },
  computed: {
    layoutStyle() {
      if (!this.currentBackground) return { backgroundColor: '#fff', color: '#000' }
      const style = {}
      if (this.currentBackground.type === 'solid') {
        style.backgroundColor = this.currentBackground.color
        style.color = this.currentBackground.textColor
      } else {
        style.backgroundImage = `url(${this.currentBackground.url})`
        style.backgroundSize = 'cover'
        style.backgroundPosition = 'center'
        style.backgroundRepeat = 'no-repeat'
        style.backgroundAttachment = 'fixed'
      }
      return style
    },
    sidebarWidth() {
      if (this.menuMode === 'tree' && this.showSidebar) {
        return this.isMobile ? (this.sidebarExpanded ? '200px' : '64px') : '200px'
      }
      return '0px'
    }
  },
  watch: {
    menuMode(newMode) {
      this.$nextTick(() => { this.updateLayout() })
    },
    '$route'() {
      if (this.menuMode === 'flow') {
        this.showSidebar = false
        this.sidebarExpanded = false
        this.$nextTick(() => {
          this.updateLayout()
          this.forceMainWidth()
          window.dispatchEvent(new Event('resize'))
        })
      } else if (this.isMobile) {
        this.closeSidebar()
      }
    },
    showSidebar(newVal) {
      if (this.menuMode === 'flow' && newVal === true) {
        this.showSidebar = false
        this.sidebarExpanded = false
      }
    }
  },
  mounted() {
    EventBus.$on('menu-mode-change', this.handleMenuModeChange)
    EventBus.$on('nav-menus-update', this.handleMenuUpdate)
    EventBus.$on('sidebar-visibility-change', this.handleSidebarVisibility)
    EventBus.$on('background-change', this.handleBackgroundChange)

    this.initBackground()
    this.checkDeviceType()

    window.addEventListener('resize', this.handleResize)
    
    this.$nextTick(() => {
      this.adjustLayoutHeight()
      if (this.menuMode === 'tree' && !this.isMobile) {
        this.showSidebar = true
        this.sidebarExpanded = true
      }
    })
  },
  beforeDestroy() {
    EventBus.$off('menu-mode-change', this.handleMenuModeChange)
    EventBus.$off('nav-menus-update', this.handleMenuUpdate)
    EventBus.$off('sidebar-visibility-change', this.handleSidebarVisibility)
    EventBus.$off('background-change', this.handleBackgroundChange)
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    checkDeviceType() {
      this.isMobile = window.innerWidth <= 768
      if (this.isMobile) {
        this.sidebarExpanded = false
        this.showSidebar = false
      } else {
        this.sidebarExpanded = true
        this.showSidebar = true
      }
    },
    handleResize() {
      clearTimeout(this.resizeTimeout)
      this.resizeTimeout = setTimeout(() => {
        const wasMobile = this.isMobile
        this.checkDeviceType()
        
        if (!wasMobile && this.isMobile) {
           this.closeSidebar()
        }
        this.adjustLayoutHeight()
        if (this.menuMode === 'flow') this.forceMainWidth()
      }, 250)
    },
    toggleSidebar() {
      this.sidebarExpanded = true
      this.showSidebar = true
      this.$nextTick(() => {
        setTimeout(() => {
          EventBus.$emit('show-sidebar-menu')
          if (this.navMenus && this.navMenus.length > 0) {
            EventBus.$emit('nav-menus-update', this.navMenus)
          }
        }, 50)
      })
    },
    closeSidebar() {
      this.sidebarExpanded = false
      this.showSidebar = false
      EventBus.$emit('hide-sidebar')
      EventBus.$emit('sidebar-visibility-change', false)
    },
    handleToggleSidebar() {
      if (this.isMobile) {
        if (this.showSidebar) {
          this.closeSidebar()
        } else {
          this.toggleSidebar()
        }
      }
    },
    initBackground() {
      const savedBg = localStorage.getItem('appBackground')
      if (savedBg) {
        this.currentBackground = JSON.parse(savedBg)
      } else {
        this.currentBackground = { title: '默认白色', color: '#ffffff', textColor: '#000000', type: 'solid' }
      }
    },
    handleBackgroundChange(background) {
      this.currentBackground = background
    },
    updateLayout() {
      if (this.menuMode === 'flow') {
        this.closeSidebar()
      } else {
        if (!this.isMobile) {
          this.showSidebar = true
          this.sidebarExpanded = true
          this.$nextTick(() => {
            EventBus.$emit('show-sidebar-menu')
            if (this.navMenus && this.navMenus.length > 0) {
              EventBus.$emit('nav-menus-update', this.navMenus)
            }
          })
        }
      }
      this.$nextTick(() => {
        this.adjustLayoutHeight()
        if (this.menuMode === 'flow') this.forceMainWidth()
      })
    },
    forceMainWidth() {
      const mainEl = this.$refs.mainContent
      if (!mainEl) return
      mainEl.style.width = '100%'
      mainEl.style.flex = '1 0 100%'
      mainEl.style.maxWidth = '100%'
      mainEl.style.minWidth = '0'
      void mainEl.offsetHeight
    },
    handleMenuModeChange(mode) {
      this.menuMode = mode
      this.showRouterMenu = mode === 'tree'
      this.updateLayout()
    },
    handleMenuUpdate(menus) {
      this.navMenus = menus
      if (menus && menus.length > 0) {
        sessionStorage.setItem('navMenus', JSON.stringify(menus))
      }
    },
    handleSidebarVisibility(visible) {
      if (this.menuMode === 'flow') {
        this.showSidebar = false
        this.sidebarExpanded = false
        return
      }
      if (this.showSidebar !== visible) {
         this.showSidebar = visible
         this.sidebarExpanded = visible
      }
    },
    handleMenuChildrenChange(children) {
      this.currentChildren = children
      this.showRouterMenu = true
    },
    adjustLayoutHeight() {
      const headerHeight = document.querySelector('.layout-header')?.offsetHeight || 60
      const mainContainerHeight = `calc(100vh - ${headerHeight}px)`
      const mainContainers = document.querySelectorAll('.main-container')
      mainContainers.forEach((container) => {
        container.style.height = mainContainerHeight
      })
    }
  }
}
</script>

<style lang="less" scoped>
.app-layout {
  height: 96vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-attachment: fixed;
}

.layout-container {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.layout-header {
  padding: 0;
  background: inherit;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  height: auto !important;
  z-index: 1001;
}

.main-container {
  height: calc(100vh - var(--header-height, 60px));
  display: flex;
  overflow: hidden;
  min-height: 0;
  flex-wrap: nowrap;
  padding: 0 !important;
  margin: 0 !important;
  position: relative;

  &.tree-mode {
    .layout-sidebar {
      height: 100%;
      overflow: hidden;
    }
  }
}

.mobile-sidebar-trigger {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 60px;
  background: #409eff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 6px 6px 0;
  z-index: 999;
  cursor: pointer;
  box-shadow: 2px 0 8px rgba(0,0,0,0.2);
  font-size: 18px;
  &:active { background: #337ecc; }
}

.sidebar-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1005;
}

.layout-sidebar {
  background: #f5f7fa;
  border-right: 1px solid #e4e7ed;
  transition: all 0.3s ease;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  &.mobile-collapsed { width: 64px !important; }
  &.mobile-expanded { width: 200px !important; }
}

.right-layout-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  height: 100%;
  z-index: 1;
}

.layout-bottom {
  flex-shrink: 0;
}

.layout-content {
  padding: 0 !important;
  background: #f0f2f5;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1; 
  min-height: 0;

  &.flow-main {
    flex: 1 0 100% !important;
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
  }
}

.content-wrapper {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 8px;
  margin: 0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  min-height: 0;
}

.router-view-container {
  flex: 1;
  overflow: auto;
  min-height: 0;
}

.router-view {
  min-height: 100%;
  box-sizing: border-box;
}

.router-menu-component {
  flex-shrink: 0;
  border-top: 1px solid #e4e7ed;
}

@media (max-width: 768px) {
  .layout-sidebar {
    position: absolute;
    z-index: 1006;
    height: 100%;
    &.mobile-collapsed { transform: translateX(-100%); }
    &.mobile-expanded { transform: translateX(0); }
  }
}

* {
  box-sizing: border-box;
}

html,
body,
#app,
.app-container,
.app-layout,
.layout-container,
.main-container {
  overflow: hidden !important;
}
</style>